import { useMemo, useState, useCallback, useEffect } from 'react';
import './Dividend.sass';
import Hero from '../components/Hero';
import Breadcrumbs from '../components/Breadcrumbs';
import heroL from '../assets/dividend/hero-l.jpg';
import magnify from '../assets/dividend/magnify.svg';
import iconList from '../assets/dividend/list.svg';
import download from '../assets/dividend/download.svg';
import none from '../assets/dividend/none.svg';
import YearMonthSelector from '../components/YearMonthSelector';
import { fetcher, useStockList } from '../api';
import { filter } from 'lodash';
import { useWindowSize } from 'react-use';
import SlideToggle from 'react-slide-toggle';
import BezierEasing from 'bezier-easing';
import Meta from '../components/Meta';
import { useLocation } from 'react-router';

const bezier = BezierEasing(0.68, 0, 0.265, 1);

const Dividend = (props) => {
	const [form, setForm] = useState({
		type: 'etf',
		onshore: 'all',
		offshore: 'all',
		etf: 'all',
		durationType: 'year',
		year: '',
		startYear: '',
		startMonth: '',
		endYear: '',
		endMonth: '',
	});

	const [slideAll, setSlideAll] = useState(true);
	const [slideAllDown, setSlideAllDown] = useState(false);
	const [slideAllUp, setSlideAllUp] = useState(false);
	const location = useLocation();
	const { width } = useWindowSize();
	const [showError, setShowError] = useState(false);
	const [currentType, setCurrentType] = useState('');

	useEffect(() => {
		if (form.durationType === 'yearmonth') {
			if (form.startYear && form.endYear) {
				if (form.startYear > form.endYear) {
					return setShowError(true);
				}

				if (form.startYear === form.endYear) {
					if (form.startMonth > form.endMonth) {
						return setShowError(true);
					}
				}
			}

			setShowError(false);
		}
	}, [form]);

	const toggleSlideAll = () => {
		if (slideAll) {
			setSlideAllDown(true);
			setTimeout(() => {
				setSlideAllDown(false);
			}, 100);
		} else {
			setSlideAllUp(true);
			setTimeout(() => {
				setSlideAllUp(false);
			}, 100);
		}

		setSlideAll(!slideAll);
	};

	const stockList = useStockList();

	const onshores = useMemo(() => {
		return filter(stockList.list, (stock) => {
			return stock.ec001 === 1 && stock.ec312 === 1;
		});
	}, [stockList.list]);

	const offshores = useMemo(() => {
		return filter(stockList.list, (stock) => {
			return stock.ec001 === 2 && stock.ec312 === 1;
		});
	}, [stockList.list]);

	const etfs = useMemo(() => {
		return filter(stockList.list, (stock) => {
			return stock.ec001 === 3 && stock.ec312 === 1;
		});
	}, [stockList.list]);

	// const prevFormYear = usePrevious(form.year);

	const years = useMemo(() => {
		if (stockList?.data?.dividendYear) {
			return stockList.data.dividendYear.sort().reverse();
		}
		return [];
	}, [stockList.data]);

	const getStockDividend = useCallback(
		(firstTime, type, year) => {
			if (showError) return false;

			const start = form.startYear + form.startMonth.toString().padStart(2, '0');
			const end = form.endYear + form.endMonth.toString().padStart(2, '0');

			let ec001 = '';
			if (form.type === 'onshore') ec001 = 1;
			if (form.type === 'offshore') ec001 = 2;
			if (form.type === 'etf') ec001 = 3;
			if (type === 'etf') ec001 = 3;

			let mfundID = '';
			if (form.type === 'onshore') mfundID = form.onshore;
			if (form.type === 'offshore') mfundID = form.offshore;
			if (form.type === 'etf') mfundID = form.etf;
			if (type === 'etf') mfundID = form.etf;

			let y = form.year;
			if (year) y = year;

			fetcher('WebSiteStockDividendRequest', {
				ec001: mfundID === 'all' ? ec001 : '',
				mfundID: mfundID === 'all' ? '' : mfundID,
				fundID: '',
				year: form.durationType === 'year' ? y : '',
				start: form.durationType === 'yearmonth' ? start : '',
				end: form.durationType === 'yearmonth' ? end : '',
			}).then((response) => {
				setStockDividend(response);
				setCurrentType(type || form.type);

				if (firstTime !== 'first') {
					setSlideAllDown(true);
					setSlideAll(false);
					setTimeout(() => {
						setSlideAllDown(false);
					}, 100);
				}

				setHideCell(form.type === 'etf' || type === 'etf');
			});
		},
		[form.durationType, form.year, form.startYear, form.startMonth, form.endYear, form.endMonth, form.onshore, form.offshore, form.etf, form.type, showError]
	);

	const [stockDividend, setStockDividend] = useState(null);
	const [hideCell, setHideCell] = useState(false);
	const [initialized, setInitialized] = useState(false);

	useEffect(() => {
		// 確保只執行一次初始化
		if (!initialized && years.length) {
			setInitialized(true);

			const type = new URLSearchParams(location.search).get('type');
			if (type && (type === 'etf' || type === 'ETF')) {
				// 如果 URL 包含 ETF 參數
				setForm(prevForm => ({ ...prevForm, type: 'etf', year: years[0] }));
				getStockDividend('first', 'etf', years[0]);
			} else {
				// 正常初始化
				setForm(prevForm => ({ ...prevForm, year: years[0] }));
				getStockDividend('first', '', years[0]);
			}
		}
	}, [initialized, years, location.search, getStockDividend]);

	// const prevLocation = usePrevious(location.search);

	return (
		<div className='Dividend'>
			<Meta data={props.routeData} />
			<Hero data={[{ pic: heroL, description: '配息資訊' }]} />
			<Breadcrumbs data={[{ url: '', name: '配息資訊', guid: 'a' }]} />

			<div className='common-pad-wrap'>
				<div className='page-title'>
					<div className='icon'>
						<img src={magnify} alt='' />
					</div>
					<div className='text'>查詢歷史配息紀錄</div>
				</div>
			</div>

			<div className='search-box'>
				<div className='group g1'>
					<div className='group-title'>選擇基金</div>

					<select
						className='category'
						value={form.type}
						onChange={(event) => {
							setForm({ ...form, type: event.target.value });
						}}>
						<option value='onshore'>境內基金</option>
						<option value='offshore'>境外基金</option>
						<option value='etf'>ETF</option>
					</select>

					{form.type === 'onshore' ? (
						<select
							className='individual'
							value={form.onshore}
							onChange={(event) => {
								setForm({ ...form, onshore: event.target.value });
							}}>
							<option value='all'>全部</option>
							{onshores.map((fund) => {
								const { guid, twName } = fund;
								return (
									<option value={guid} key={guid}>
										{twName}
									</option>
								);
							})}
						</select>
					) : null}

					{form.type === 'offshore' ? (
						<select
							className='individual'
							value={form.offshore}
							onChange={(event) => {
								setForm({ ...form, offshore: event.target.value });
							}}>
							<option value='all'>全部</option>
							{offshores.map((fund) => {
								const { guid, twName } = fund;
								return (
									<option value={guid} key={guid}>
										{twName}
									</option>
								);
							})}
						</select>
					) : null}

					{form.type === 'etf' ? (
						<select
							className='individual'
							value={form.etf}
							onChange={(event) => {
								setForm({ ...form, etf: event.target.value });
							}}>
							<option value='all'>全部</option>
							{etfs.map((fund) => {
								const { guid, twName } = fund;
								return (
									<option value={guid} key={guid}>
										{twName}
									</option>
								);
							})}
						</select>
					) : null}
				</div>

				<SlideToggle
					duration={300}
					easeCollapse={bezier}
					easeExpand={bezier}
					expandEvent={width >= 768}
					render={({ toggle, setCollapsibleElement, toggleState }) => {
						return (
							<div className={`slidetoggle ${toggleState}`}>
								<div className='toggle-target' ref={setCollapsibleElement}>
									<div className='flex-between'>
										<div className='group'>
											<div className='group-title'>配息期間</div>

											<div
												className='radio-wrap'
												onClick={() => {
													setForm({ ...form, durationType: 'year' });
												}}>
												<input
													type='radio'
													name='durationType'
													value='year'
													checked={form.durationType === 'year'}
													onChange={() => {
														setForm({ ...form, durationType: 'year' });
													}}
												/>

												<div className='outer'>
													<div className='inner'></div>
												</div>

												<div className='text'>
													<div className='label'>年度配息</div>

													<select
														className='year'
														onChange={(event) => {
															setForm({ ...form, year: event.target.value });
														}}>
														{years.map((year) => {
															return (
																<option value={year} key={year}>
																	{year}年
																</option>
															);
														})}
													</select>
												</div>
											</div>

											<div
												className='radio-wrap'
												htmlFor='radioYearMonth'
												onClick={() => {
													setForm({ ...form, durationType: 'yearmonth' });
												}}>
												<input
													type='radio'
													name='durationType'
													value='yearmonth'
													checked={form.durationType === 'yearmonth'}
													onChange={() => {
														setForm({ ...form, durationType: 'yearmonth' });
													}}
												/>

												<div className='outer'>
													<div className='inner'></div>
												</div>

												<div className='text'>
													<div className='label'>自訂期間</div>

													<div className='flex-mob'>
														<YearMonthSelector
															selectedYear={form.startYear}
															selectedMonth={form.startMonth}
															updateValues={(year, month) => {
																setForm({ ...form, startYear: year, startMonth: month });
															}}
														/>

														<div className='to'></div>

														<YearMonthSelector
															selectedYear={form.endYear}
															selectedMonth={form.endMonth}
															updateValues={(year, month) => {
																setForm({ ...form, endYear: year, endMonth: month });
															}}
														/>

														{showError ? <div className='error-message'>查詢結束日需大於起始日</div> : null}
													</div>
												</div>
											</div>
										</div>

										<div className='button-wrap'>
											<div className='common-button' onClick={getStockDividend}>
												查詢
											</div>
										</div>
									</div>
								</div>

								<div className='slide-control top' onClick={toggle}>
									<div className='text'>{toggleState === 'EXPANDED' ? '收合' : '展開'}篩選條件</div>
									<div className='icon'></div>
								</div>
							</div>
						);
					}}
				/>
			</div>

			{stockDividend ? (
				<div className='result-box'>
					{stockDividend.result.length ? (
						<div className='toggle-control'>
							<div className='controller' onClick={toggleSlideAll}>
								<div className='icon'>
									<img src={iconList} alt='' />
								</div>
								<div className='text'>{slideAll ? '全部展開' : '全部收合'}</div>
							</div>
						</div>
					) : (
						<div className='nothing'>
							<div className='img'>
								<img src={none} alt='' />
							</div>
							<div className='text'>無符合搜尋條件的配息資料</div>
						</div>
					)}

					{stockDividend.result.map((mfund, i) => {
						return (
							<div className='result' key={`${mfund.guid}${i}`}>
								<div className='item'>
									<SlideToggle
										duration={300}
										easeCollapse={bezier}
										easeExpand={bezier}
										collapsed={true}
										expandEvent={slideAllDown}
										collapseEvent={slideAllUp}
										render={({ toggle, setCollapsibleElement, toggleState }) => {
											return (
												<div className={`slidetoggle ${toggleState}`}>
													<div className='head'>
														<div className='name' onClick={toggle}>
															{mfund.twName}
														</div>
														<div className='icon-wrap' onClick={toggle}>
															<div className={`icon ${toggleState}`}></div>
														</div>
													</div>

													<div className='toggle-target' ref={setCollapsibleElement}>
														<div className='dividend-table-wrap'>
															<table>
																<thead>
																	<tr>
																		{hideCell ? null : (<th className='left'>基金級別</th>)}
																		<th>配息幣別</th>
																		<th>每單位配息金額</th>
																		{currentType === 'etf' && <th>評價日</th>}
																		<th>除息日</th>
																		{currentType !== 'etf' && <th>配息基準日</th>}
																		<th>配息發放日</th>
																		{currentType === 'etf' ? null : (
																			<th>本次配息率</th>
																		)}
																		<th>配息頻率</th>
																	</tr>
																</thead>

																<tbody>
																	{mfund.result?.map((row, ii) => {
																		const { guid, ec018, ec006, ec01022, ec01013, ec01031, ec01034, ec01035, ec01012, ec01036 } = row;

																		return (
																			<tr key={mfund.guid + guid + ii}>
																				{hideCell ? null : (<td className='left'>{ec018}</td>)}
																				<td>{ec006}</td>
																				<td>{ec01022}</td>
																				{currentType === 'etf' && <td>{ec01036}</td>}
																				<td>{ec01034}</td>
																				{currentType !== 'etf' && <td>{ec01013}</td>}
																				<td>{ec01031}</td>
																				{currentType === 'etf' ? null : (
																					<td>{ec01035}</td>
																				)}
																				<td>{ec01012}</td>
																			</tr>
																		);
																	})}
																</tbody>
															</table>

															{mfund.ec003 ? <div className='warning'>*{mfund.ec003}</div> : null}
														</div>
													</div>
												</div>
											);
										}}
									/>
								</div>
							</div>
						);
					})}

					<div className='downloads'>
						<a href={stockDividend.pdf01?.url} className='download' target='_blank' rel='noreferrer noopener'>
							<div className='icon'>
								<img src={download} alt='' />
							</div>

							<div className='texts'>
								<div className='top'>{stockDividend.pdf01?.name}</div>
								<div className='bot'>配息組成項目表</div>
							</div>
						</a>

						<a href={stockDividend.pdf02?.url} className='download' target='_blank' rel='noreferrer noopener'>
							<div className='icon'>
								<img src={download} alt='' />
							</div>

							<div className='texts'>
								<div className='top'>{stockDividend.pdf02?.name}</div>
								<div className='bot'>配息組成項目表</div>
							</div>
						</a>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default Dividend;
